import {FormikProvider, useFormik, getIn} from "formik";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {Button, Form, InputNumber, Switch, Table, TimePicker} from "antd";
import localeTr from "antd/es/date-picker/locale/tr_TR";
import localeEn from "antd/es/date-picker/locale/en_US";

import {useStore} from "../../../../hooks/useStore";
import {ErrorFocus} from "../../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CompanyButtonContainer} from "../optionsStyles";
import cutOffOptionsValidations from "./cutOffOptionsValidations";
import {EmptyMessage} from "../../../_defaultComponents/EmptyMessage/EmptyMessage";

const Options = ({companyLoading, companyData}) => {
    const stores = useStore();
    const {t, i18n} = useTranslation(["cutOffOptions"]);
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);

    const cutOff = companyData?.data?.options?.cutOff || {
        isActive: false,
        time: {hour: 16, minute: 0},
        pickupTime: 3,
        handOverTime: 5,
        maxOrder: 20
    };
    const formik = useFormik({
        initialValues: {
            ...(companyData && companyData.data),
            options: {
                cutOff: {
                    isActive: cutOff?.isActive,
                    time: cutOff?.time,
                    pickupTime: cutOff?.pickupTime,
                    handOverTime: cutOff?.handOverTime,
                    maxOrder: cutOff?.maxOrder
                }
            }
        },
        enableReinitialize: true,
        validateOnChange: true,
        validationSchema: cutOffOptionsValidations,
        onSubmit: async values => {
            try {
                return await stores.companyStore.updateCutOffOptions(values);
            } catch (e) {
                console.log(e);
            }
        }
    });
    const {values, errors, touched, handleSubmit, isSubmitting, setFieldValue} = formik;
    const getTitleText = (key, showShort = false) => {
        let text = t(`cutOffOptions.fields.${key}.label`, {ns: "cutOffOptions"});
        if (showShort)
            text = `${text} (${t(`cutOffOptions.fields.${key}.short`, {ns: "cutOffOptions"})})`;
        return text;
    };

    const getTime = time =>
        time?.hour?.toString().length > 0
            ? moment(`${time.hour}:${time.minute}`, "HH:mm")
            : moment().set({hour: 0, minute: 50});

    const hasError = key => {
        const getInErrors = getIn(errors, `options.cutOff.${key}`);
        const getInTouch = getIn(touched, `options.cutOff.${key}`);
        return getInErrors && getInTouch ? getInErrors : false;
    };

    return (
        <Form
            style={{width: "100%"}}
            autoComplete="off"
            onFinish={handleSubmit}
            data-testid="barcodeOptionsForm"
            onChange={() => setIsCompanyFormChanged(true)}
        >
            <Table
                loading={companyLoading}
                dataSource={[companyData.data]}
                rowKey="_id"
                pagination={false}
                data-testid="cutOffOptionsTable"
                locale={{
                    emptyText: <EmptyMessage />
                }}
            >
                <ErrorFocus formik={formik} />
                <FormikProvider value={formik}>
                    <Table.Column
                        dataIndex="isActive"
                        title={getTitleText("isActive")}
                        render={() => (
                            <Form.Item>
                                <Switch
                                    value={values.options.cutOff?.isActive}
                                    checked={values.options.cutOff?.isActive}
                                    checkedChildren={t("other.active", {ns: "common"})}
                                    unCheckedChildren={t("other.passive", {ns: "common"})}
                                    data-testid="cutOffIsActive"
                                    onChange={e => {
                                        setIsCompanyFormChanged(true);
                                        setFieldValue("options.cutOff.isActive", e);
                                    }}
                                />
                            </Form.Item>
                        )}
                    />
                    <Table.Column
                        dataIndex="cutOffTime"
                        title={getTitleText("cutOffTime")}
                        render={() => {
                            const onChange = time => {
                                setFieldValue("options.cutOff.time.hour", time.hour());
                                setFieldValue("options.cutOff.time.minute", time.minute());
                                setIsCompanyFormChanged(true);
                            };
                            return (
                                <Form.Item>
                                    <TimePicker
                                        value={getTime(values.options?.cutOff?.time)}
                                        format="HH:mm"
                                        allowClear={false}
                                        onSelect={onChange}
                                        onChange={onChange}
                                        disabled={!values.options.cutOff?.isActive}
                                        locale={
                                            i18n?.resolvedLanguage === "en" ? localeEn : localeTr
                                        }
                                    />
                                </Form.Item>
                            );
                        }}
                    />
                    <Table.Column
                        dataIndex="pickupTime"
                        title={getTitleText("pickupTime")}
                        render={() => {
                            const error = hasError("pickupTime");
                            return (
                                <Form.Item
                                    validateStatus={error ? "error" : "success"}
                                    help={error ? getIn(errors, `options.cutOff.pickUpTime`) : null}
                                >
                                    <InputNumber
                                        type="number"
                                        name="pickupTime"
                                        value={getIn(values, "options.cutOff.pickupTime")}
                                        min="1"
                                        disabled={!values.options?.cutOff?.isActive}
                                        onChange={value => {
                                            setFieldValue("options.cutOff.pickupTime", value);
                                            setIsCompanyFormChanged(true);
                                        }}
                                    />
                                </Form.Item>
                            );
                        }}
                    />
                    <Table.Column
                        dataIndex="handOverTime"
                        title={getTitleText("handOverTime", true)}
                        render={() => {
                            const error = hasError("handOverTime");
                            return (
                                <Form.Item
                                    validateStatus={error && getInTouch ? "error" : "success"}
                                    help={
                                        error ? getIn(errors, `options.cutOff.handOverTime`) : null
                                    }
                                >
                                    <InputNumber
                                        type="number"
                                        name="handOverTime"
                                        value={values.options?.cutOff?.handOverTime}
                                        min="1"
                                        disabled={!values.options.cutOff?.isActive}
                                        onChange={value => {
                                            setIsCompanyFormChanged(true);
                                            setFieldValue("options.cutOff.handOverTime", value);
                                        }}
                                    />
                                </Form.Item>
                            );
                        }}
                    />
                    <Table.Column
                        dataIndex="maxOrder"
                        title={getTitleText("maxOrder", true)}
                        render={() => {
                            const error = hasError("maxOrder");
                            return (
                                <Form.Item
                                    validateStatus={error ? "error" : "success"}
                                    help={error ? getIn(errors, `options.cutOff.maxOrder`) : null}
                                >
                                    <InputNumber
                                        type="number"
                                        name="maxOrder"
                                        value={values.options?.cutOff?.maxOrder}
                                        min="1"
                                        disabled={!values.options.cutOff?.isActive}
                                        onChange={value => {
                                            setFieldValue("options.cutOff.maxOrder", value);
                                            setIsCompanyFormChanged(true);
                                        }}
                                    />
                                </Form.Item>
                            );
                        }}
                    />
                </FormikProvider>
            </Table>
            <CompanyButtonContainer justify="end" align="middle">
                <Button
                    htmlType="submit"
                    disabled={!isCompanyFormChanged}
                    type="primary"
                    loading={isSubmitting}
                    data-testid="submitButton"
                >
                    {t("actions.save", {ns: "common"})}
                </Button>
            </CompanyButtonContainer>
        </Form>
    );
};

export default Options;
